import React, { useState, useMemo } from "react";

// Sunday-first, matching the dashboard-wide convention (DateRangePicker,
// Website Analytics, CDP, Google Ads Day of Week).
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Format a local Date as YYYY-MM-DD without UTC shift */
const toLocalDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const DemandCalendarPanel = ({ data, loading, onLookaheadChange, onCellClick, activeDate, startDate: startDateProp }) => {
  const [lookahead, setLookahead] = useState(90);

  const handleToggle = (days) => {
    setLookahead(days);
    onLookaheadChange?.(days);
  };

  const { weeks, maxSessions } = useMemo(() => {
    if (!data?.length) return { weeks: [], maxSessions: 0 };

    const dateMap = {};
    let max = 0;
    for (const row of data) {
      dateMap[row.search_date] = row;
      if (row.total_sessions > max) max = row.total_sessions;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Start from the 1st of the month based on the date picker's start date
    const filterDate = startDateProp ? new Date(startDateProp + "T00:00:00") : today;
    const firstOfMonth = new Date(filterDate.getFullYear(), filterDate.getMonth(), 1);

    // Pad leading empty cells for days before the 1st (Sun=0 padding cells)
    const firstDow = firstOfMonth.getDay(); // 0=Sun
    const leadingPad = firstDow; // number of empty cells before the 1st

    const endDate = new Date(firstOfMonth);
    endDate.setDate(endDate.getDate() + lookahead);

    const weeksArr = [];
    let currentWeek = [];
    const cursor = new Date(firstOfMonth);

    const todayStr = toLocalDateStr(today);

    // Add leading empty cells for days before the 1st
    for (let i = 0; i < leadingPad; i++) {
      currentWeek.push({ date: null, dayOfWeek: i, isPast: false, isToday: false, data: null });
    }

    while (cursor <= endDate) {
      const key = toLocalDateStr(cursor);
      const dayOfWeek = cursor.getDay(); // 0=Sun, 6=Sat
      const isPast = cursor < today;
      const isToday = key === todayStr;

      currentWeek.push({ date: key, dayOfWeek, isPast, isToday, data: dateMap[key] || null });

      if (dayOfWeek === 6) {
        weeksArr.push(currentWeek);
        currentWeek = [];
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push({ date: null, dayOfWeek: currentWeek.length, isPast: false, isToday: false, data: null });
      }
      weeksArr.push(currentWeek);
    }

    return { weeks: weeksArr, maxSessions: max };
  }, [data, lookahead]);

  const getCellStyle = (sessions, maxSess) => {
    if (!sessions || maxSess === 0) return { backgroundColor: "rgba(229,231,235,0.3)" }; // gray-200/30
    const intensity = Math.min(sessions / maxSess, 1);
    // 5-step ramp using app indigo palette (same as geography map)
    const alpha = 0.12 + intensity * 0.78;
    return { backgroundColor: `rgba(129, 140, 248, ${alpha})` }; // indigo-400
  };

  const isPricingOpportunity = (row) => {
    return row && row.total_sessions >= 10 && row.conversion_pct < 2.0;
  };

  const formatTooltipDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex gap-1">
            {Array.from({ length: 7 }).map((_, j) => (
              <div key={j} className="flex-1 h-8 bg-gray-200 dark:bg-gray-800 rounded" />
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
        No future date searches for this period.
      </div>
    );
  }

  return (
    <div>
      {/* Lookahead toggle */}
      <div className="flex items-center gap-1 mb-4">
        {[30, 60, 90].map((d) => (
          <button
            key={d}
            onClick={() => handleToggle(d)}
            className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
              lookahead === d
                ? "bg-[#32B1FF] text-white"
                : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
            }`}
          >
            {d} days
          </button>
        ))}
      </div>

      {/* Day headers */}
      <div className="flex gap-1 mb-1">
        {DAY_LABELS.map((d) => (
          <div key={d} className="flex-1 text-center text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase">
            {d}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="space-y-1">
        {weeks.map((week, wi) => {
          // Check if this week starts a new month
          const firstDate = week.find((c) => c.date)?.date;
          const prevWeekLastDate = wi > 0 ? [...weeks[wi - 1]].reverse().find((c) => c.date)?.date : null;
          const curMonth = firstDate ? new Date(firstDate + "T00:00:00").getMonth() : null;
          const prevMonth = prevWeekLastDate ? new Date(prevWeekLastDate + "T00:00:00").getMonth() : null;
          const showMonthLabel = wi === 0 || (curMonth !== null && curMonth !== prevMonth);
          const monthName = firstDate
            ? new Date(firstDate + "T00:00:00").toLocaleDateString("en-US", { month: "long", year: "numeric" })
            : "";

          return (
          <div key={wi}>
            {showMonthLabel && (
              <div className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mt-2 mb-1">
                {monthName}
              </div>
            )}
            <div className="flex gap-1">
            {week.map((cell, ci) => {
              if (!cell.date) {
                return <div key={ci} className="flex-1 h-8" />;
              }
              const row = cell.data;
              const sessions = row?.total_sessions || 0;
              const hasPricingOpp = isPricingOpportunity(row);

              const isActive = activeDate === cell.date;
              return (
                <div
                  key={ci}
                  onClick={() => {
                    if (!cell.isPast && sessions > 0 && onCellClick) {
                      onCellClick(isActive ? "" : cell.date);
                    }
                  }}
                  className={`flex-1 h-8 rounded relative group transition-colors
                    ${!cell.isPast && sessions > 0 && onCellClick ? "cursor-pointer" : "cursor-default"}
                    ${cell.isPast ? "opacity-30" : ""}
                    ${isActive ? "ring-2 ring-[#32B1FF]" : cell.isToday ? "ring-1 ring-[#32B1FF]/50" : ""}
                    ${hasPricingOpp ? "ring-1 ring-amber-400" : ""}
                  `}
                  style={getCellStyle(sessions, maxSessions)}
                  title={row
                    ? `${formatTooltipDate(cell.date)}\n${sessions} sessions searched · ${row.booked_sessions} booked\n${row.conversion_pct}% conversion${row.avg_nightly_rate ? `\n$${row.avg_nightly_rate} avg nightly rate` : ""}${hasPricingOpp ? "\n⚡ High demand, low conversion" : ""}`
                    : cell.date
                  }
                >
                  <span className="absolute inset-0 flex items-center justify-center text-sm text-gray-600 dark:text-gray-300 font-medium">
                    {parseInt(cell.date.split("-")[2])}
                  </span>
                </div>
              );
            })}
            </div>
          </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
        <span className="text-sm text-gray-500 dark:text-gray-400">Less</span>
        <div className="flex gap-0.5">
          <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: "rgba(129,140,248,0.12)" }} />
          <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: "rgba(129,140,248,0.38)" }} />
          <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: "rgba(129,140,248,0.65)" }} />
          <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: "rgba(129,140,248,0.90)" }} />
        </div>
        <span className="text-sm text-gray-500 dark:text-gray-400">More</span>
        <div className="flex items-center gap-1 ml-2">
          <div className="w-3 h-3 rounded-sm ring-1 ring-amber-400" style={{ backgroundColor: "rgba(129,140,248,0.12)" }} />
          <span className="text-sm text-gray-500 dark:text-gray-400">Pricing opportunity</span>
        </div>
      </div>
    </div>
  );
};

export default DemandCalendarPanel;
